"use client";

/**
 * AppShell — 통합 layout (사용자 결정 2026-05-09).
 *
 * 데스크탑: AppSidebar (좌) + main + RightRail (우) + RightRailResizer
 * 모바일 (≤720px): BottomNav (하단 footer) + main. AppSidebar 완전 unmount (사용자 결정 2026-05-21).
 */

import { Suspense, useEffect, useState } from "react";
import { AppSidebar } from "./app-sidebar";
import { AdminBanner } from "./admin-banner";
import { TabBar } from "./tab-bar";
import { RightRail } from "./right-rail";
import { RightRailResizer } from "./right-rail-resizer";
import { BottomNav } from "./bottom-nav";
import { TabsProvider } from "@/contexts/tabs-context";

const MOBILE_BREAKPOINT = 720;
const FOOTER_H = 56;
const HEADER_H = 44; // TabBar 높이 (RightRail pt 와 동일 — TabBar 가 main 의 first child)

export function AppShell({ children, minimal = false }: { children: React.ReactNode; minimal?: boolean }) {
  // 모바일 — useEffect 안 set (SSR/client 모두 false 시작 → mismatch 없음)
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth <= MOBILE_BREAKPOINT);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  if (minimal) {
    return <main className="bite-scope bite-light min-h-screen">{children}</main>;
  }

  return (
    <TabsProvider>
      <AdminBanner />
      {/* 모바일에선 사이드바/RightRail/Resizer 모두 unmount — 메모리/DOM 절약, CSS 충돌 X */}
      {!isMobile && (
        <>
          <Suspense fallback={null}>
            <AppSidebar />
          </Suspense>
          <Suspense fallback={null}>
            <RightRail />
          </Suspense>
          <RightRailResizer />
        </>
      )}
      <main className="bs-main min-h-screen bg-[var(--bs-gray-50)] text-[var(--bs-ink)]">
        <TabBar />
        {children}
      </main>
      {/* 모바일에서만 하단 footer nav */}
      {isMobile && <BottomNav />}
      <style jsx global>{`
        body { display: block; min-height: unset; }
        :root { --bs-rr-width: 320px; }
        .bs-main { padding-left: 260px; padding-right: var(--bs-rr-width); transition: padding-left 200ms ease; }
        body:has(.app-sb.collapsed) .bs-main { padding-left: 76px; }
        @media (max-width: 1100px) {
          .bs-main { padding-right: 0; }
        }
        @media (max-width: ${MOBILE_BREAKPOINT}px) {
          /* 모바일 main body = viewport - 헤더(TabBar) - 푸터. 사용자 보고 2026-05-21:
             "헤더 영역도 안 제외하고 계산했지" → TabBar 44px 까지 제외. */
          .bs-main {
            padding-left: 0;
            padding-bottom: calc(${FOOTER_H}px + env(safe-area-inset-bottom));
            min-height: calc(100vh - ${HEADER_H}px - ${FOOTER_H}px - env(safe-area-inset-bottom));
          }
        }
      `}</style>
    </TabsProvider>
  );
}
